// The venue's markets as the page keeps them: the trading state polled every two seconds, the
// protocol's figures, the tape, and each market's mark (the oracle's price: Hyperliquid's BBO mid
// at the market's scale), live from the book with the chain's own BBO as the fallback.

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { pt, type Instrument, type TradingState } from "../sdk";
import { bbos, hold } from "../hl/feed";
import { createStore, useStore } from "./store";

export const POLL = { trading: 2_000, tape: 3_000, summary: 15_000, volumes: 30_000, queue: 15_000 } as const;

export function useTradingState() {
  return useQuery({ queryKey: ["tradingState"], queryFn: pt.tradingState, refetchInterval: POLL.trading, staleTime: 1_000 });
}

export function useInstruments(): Instrument[] {
  return useTradingState().data?.instruments ?? EMPTY;
}
const EMPTY: Instrument[] = [];

export function useInstrument(symbol: string): Instrument | undefined {
  return useInstruments().find((instrument) => instrument.symbol === symbol);
}

export function useInstrumentById(id: number): Instrument | undefined {
  return useInstruments().find((instrument) => instrument.id === id);
}

export function useSummary() {
  return useQuery({ queryKey: ["summary"], queryFn: pt.summary, refetchInterval: POLL.summary });
}

/** 24h volume by instrument id, raw. */
export function useVolumes() {
  return useQuery({
    queryKey: ["volumes"],
    queryFn: async () => Object.fromEntries(await pt.volumes()) as Record<number, string>,
    refetchInterval: POLL.volumes,
  });
}

export function useRecentTrades() {
  return useQuery({ queryKey: ["recentTrades"], queryFn: pt.recentTrades, refetchInterval: POLL.tape });
}

export function useQueue() {
  return useQuery({ queryKey: ["queue"], queryFn: pt.queue, refetchInterval: POLL.queue });
}

/** The Exchange's win fee, read on chain once; the SDK's previews use it from then on. */
export function useWinFee() {
  return useQuery({ queryKey: ["winFee"], queryFn: pt.winFeeRate, staleTime: Infinity, retry: 3 });
}

export type Mark = {
  /** Raw at the market's scale. */
  raw: string;
  /** The touch, raw, when the book gave it. */
  bid: string | null;
  ask: string | null;
  source: "book" | "chain" | "frozen";
  at: number;
};

export const marks = createStore<Record<number, Mark>>({});

/** Each listed market's symbol by id, for words outside React. */
export const symbols = new Map<number, string>();
/** Each listed market's price scale by id, for drawing outside React. */
export const scales = new Map<number, string>();

export function useMark(instrumentId: number | undefined): Mark | undefined {
  return useStore(marks, (all) => (instrumentId === undefined ? undefined : all[instrumentId]));
}

/** Every mark as `{instrumentId: raw}`, for the SDK's valuations. */
export function markMap(all: Record<number, Mark>): Record<string, string> {
  return Object.fromEntries(Object.entries(all).map(([id, mark]) => [id, mark.raw]));
}

const STALE_MS = 10_000;

function setMark(id: number, mark: Mark) {
  marks.set((all) => {
    const held = all[id];
    if (held && held.raw === mark.raw && held.source === mark.source && held.bid === mark.bid) return all;
    return { ...all, [id]: mark };
  });
}

function fromBook(instrument: Instrument, state: Record<string, import("../hl/feed").Bbo>) {
  const touch = state[instrument.symbol];
  if (!touch?.bid || !touch.ask) return;
  const raw = pt.midOf(touch.bid.px, touch.ask.px, instrument.priceScale);
  if (!raw) return;
  const bid = pt.midOf(touch.bid.px, touch.bid.px, instrument.priceScale);
  const ask = pt.midOf(touch.ask.px, touch.ask.px, instrument.priceScale);
  setMark(instrument.id, { raw, bid, ask, source: "book", at: Date.now() });
}

/** Keeps every listed market's mark: mounted once by the app. */
export function useMarkFeed(state: TradingState | undefined) {
  const instruments = state?.instruments;
  const identity =
    instruments?.map((instrument) => `${instrument.id}:${instrument.symbol}:${instrument.terminal}`).join(",") ?? "";

  useEffect(() => {
    if (!instruments) return;
    instruments.forEach((instrument) => {
      symbols.set(instrument.id, instrument.symbol);
      scales.set(instrument.id, instrument.priceScale);
    });
    const live = instruments.filter((instrument) => !instrument.terminal);
    for (const instrument of instruments) {
      if (instrument.terminal && instrument.frozenMid) {
        setMark(instrument.id, { raw: instrument.frozenMid, bid: null, ask: null, source: "frozen", at: Date.now() });
      }
    }
    const releases = live.map((instrument) => hold({ type: "bbo", coin: instrument.symbol }));
    const read = () => live.forEach((instrument) => fromBook(instrument, bbos.get()));
    read();
    const unsubscribe = bbos.subscribe(read);

    // the book quiet for too long: the chain's own BBO, as the oracle reads it
    let busy = false;
    const fallback = setInterval(async () => {
      if (busy) return;
      busy = true;
      try {
        for (const instrument of live) {
          const held = marks.get()[instrument.id];
          if (held && Date.now() - held.at < STALE_MS) continue;
          const quote = await pt.quote(instrument.assetIndex);
          setMark(instrument.id, { raw: quote.mid, bid: quote.bid, ask: quote.ask, source: "chain", at: Date.now() });
        }
      } catch {
        // the next tick tries again
      } finally {
        busy = false;
      }
    }, 3_000);

    return () => {
      releases.forEach((release) => release());
      unsubscribe();
      clearInterval(fallback);
    };
    // the instruments' identities, not each poll's new array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);
}
